import { trimmedOrUndefined } from "@/common/helpers/string.util";
import type { ValidatedAddress } from "@/pages/Replacement/replacement.types";
import type {
  AddressFieldKey,
  SuggestedAddress,
  ValidationAddress,
} from "@/services/address-validation/address-validation.types";
import type {
  AddressFormField,
  AddressFormValues,
  AddressPrefix,
} from "./choose-delivery-form.types";

const toValidationAddress = (address: AddressFormValues): ValidationAddress => ({
  country: address.country.trim(),
  line1: trimmedOrUndefined(address.line1),
  line2: trimmedOrUndefined(address.line2),
  line3: trimmedOrUndefined(address.line3),
  city: trimmedOrUndefined(address.city),
  state: trimmedOrUndefined(address.state),
  zip: trimmedOrUndefined(address.zip),
  county: trimmedOrUndefined(address.county),
  phoneNumber: trimmedOrUndefined(address.phoneNumber),
});

/**
 * Turns what the employee typed into the address sent to HOP for validation, and saved as is when HOP accepts it.
 * Values are trimmed and blank fields left out; the phone country code and delivery instructions are kept beside
 * it, since HOP's validation takes neither.
 */
export const toValidatedAddress = (entered: AddressFormValues): ValidatedAddress => ({
  address: toValidationAddress(entered),
  phoneCountryCode: entered.countryCode.trim(),
  deliveryInstructions: trimmedOrUndefined(entered.deliveryInstructions),
});

/**
 * The address saved when the employee confirms HOP's suggestion: HOP's fields exactly as suggested, plus the
 * validated phone, which HOP never suggests. Fields HOP leaves out stay out.
 */
export const withSuggestedAddress = (
  validated: ValidatedAddress,
  suggestion: SuggestedAddress
): ValidatedAddress => ({
  ...validated,
  address: {
    ...suggestion,
    country: suggestion.country ?? validated.address.country,
    phoneNumber: validated.address.phoneNumber,
  },
});

/** Puts a saved address back into the form, so the employee edits what was saved. */
export const toFormAddress = ({
  address,
  phoneCountryCode,
  deliveryInstructions,
}: ValidatedAddress): AddressFormValues => ({
  country: address.country,
  line1: address.line1 ?? "",
  line2: address.line2 ?? "",
  line3: address.line3 ?? "",
  city: address.city ?? "",
  state: address.state ?? "",
  zip: address.zip ?? "",
  county: address.county ?? "",
  countryCode: phoneCountryCode,
  phoneNumber: address.phoneNumber ?? "",
  deliveryInstructions: deliveryInstructions ?? "",
});

/**
 * Maps the address validation error keys to this step's form fields, so HOP's field errors land under the
 * fields of the address that was validated. Checkout keeps its own map for its own form.
 */
export const addressFieldsFor = (
  prefix: AddressPrefix
): Record<AddressFieldKey, AddressFormField> => ({
  addressLine1: `${prefix}.line1`,
  addressLine2: `${prefix}.line2`,
  addressLine3: `${prefix}.line3`,
  city: `${prefix}.city`,
  state: `${prefix}.state`,
  postalCode: `${prefix}.zip`,
  country: `${prefix}.country`,
  county: `${prefix}.county`,
  phoneNumber: `${prefix}.phoneNumber`,
});

/** The saved address shown back to the employee: street lines, then city and postal code. */
export const formatSavedAddress = ({ address }: ValidatedAddress): string =>
  [
    address.line1,
    address.line2,
    address.line3,
    [address.city, address.zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
